// src/hooks/useModalStates.ts
import React, { useState, useCallback } from 'react';
import { Chapter, ImageGenerationPromptData, ImageGenerationContextType } from '../types';

export interface ModalControls {
    // API Key Modal
    isApiKeyModalOpen: boolean;
    apiKeyInputValue: string; // Legacy single key (for backward compatibility)
    openaiApiKeyInputValue: string;
    deepseekApiKeyInputValue: string;
    openApiKeyModal: (currentKey: string | null) => void;
    closeApiKeyModal: () => void;
    setApiKeyInputValue: React.Dispatch<React.SetStateAction<string>>;
    setOpenaiApiKeyInputValue: React.Dispatch<React.SetStateAction<string>>;
    setDeepseekApiKeyInputValue: React.Dispatch<React.SetStateAction<string>>;

    // Cover Modal (now more generic for image generation)
    isImageGenerationModalOpen: boolean;
    imageGenerationPrompt: string | ImageGenerationPromptData;
    generatedImageDataUrl: string | null; // Renamed from generatedCoverImage
    imageGenerationContext: ImageGenerationContextType;
    openImageGenerationModal: (context: ImageGenerationContextType, initialPrompt?: string | ImageGenerationPromptData) => void;
    closeImageGenerationModal: () => void;
    setImageGenerationPrompt: React.Dispatch<React.SetStateAction<string | ImageGenerationPromptData>>;
    setGeneratedImageDataUrl: React.Dispatch<React.SetStateAction<string | null>>;
    setIsImageGenerationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    
    // Save As Modal
    isSaveAsModalOpen: boolean;
    saveAsProjectName: string;
    openSaveAsModal: (currentProjectName?: string | null) => void;
    closeSaveAsModal: () => void;
    setSaveAsProjectName: React.Dispatch<React.SetStateAction<string>>;

    // Load Project Modal
    isLoadProjectModalOpen: boolean;
    openLoadProjectModal: () => void;
    closeLoadProjectModal: () => void;

    // Split Chapter Modal
    isSplitChapterModalOpen: boolean;
    chapterToSplit: Chapter | null;
    openSplitChapterModal: (chapter: Chapter) => void;
    closeSplitChapterModal: () => void;

    // Merge Chapter Modal
    isMergeModalOpen: boolean;
    chaptersToDisplayInMergeModal: Chapter[];
    openMergeModal: (chapters: Chapter[]) => void;
    closeMergeModal: () => void;

    // Debug Response Modal
    isDebugResponseModalOpen: boolean;
    llmRawResponseForDebug: string;
    openDebugResponseModal: (rawResponse: string) => void;
    closeDebugResponseModal: () => void;

    // Legacy Cover Modal fields (to be deprecated or integrated if still directly used by ProjectHeader)
    isCoverModalOpen: boolean; // Keep for now if ProjectHeader uses it directly
    coverPrompt: string; // Keep for now
    generatedCoverImage: string | null; // Keep for now
    setGeneratedCoverImage: React.Dispatch<React.SetStateAction<string | null>>; // ADDED
    openCoverModal: () => void; // Keep for now
    closeCoverModal: () => void; // Keep for now
    setCoverPrompt: React.Dispatch<React.SetStateAction<string>>; // Keep for now
}

export const useModalStates = (initialApiKey: string | null): ModalControls => {
    // API Key Modal
    const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
    const [apiKeyInputValue, setApiKeyInputValue] = useState(initialApiKey || '');
    const [openaiApiKeyInputValue, setOpenaiApiKeyInputValue] = useState('');
    const [deepseekApiKeyInputValue, setDeepseekApiKeyInputValue] = useState('');
    
    const openApiKeyModal = useCallback((currentKey: string | null) => {
        setApiKeyInputValue(currentKey || '');
        // Keys are never pre-populated from env vars — they come from the user's saved profile.
        // VITE_OPENAI_API_KEY / VITE_DEEPSEEK_API_KEY must remain empty in .env to avoid
        // baking secrets into the public JS bundle.
        setIsApiKeyModalOpen(true);
    }, []);
    const closeApiKeyModal = useCallback(() => setIsApiKeyModalOpen(false), []);

    // Generic Image Generation Modal
    const [isImageGenerationModalOpen, setIsImageGenerationModalOpen] = useState(false);
    const [imageGenerationPrompt, setImageGenerationPrompt] = useState<string | ImageGenerationPromptData>('');
    const [generatedImageDataUrl, setGeneratedImageDataUrl] = useState<string | null>(null);
    const [imageGenerationContext, setImageGenerationContext] = useState<ImageGenerationContextType>('coverArt');

    const openImageGenerationModal = useCallback((context: ImageGenerationContextType, initialPrompt: string | ImageGenerationPromptData = '') => {
        setImageGenerationContext(context);
        setImageGenerationPrompt(initialPrompt);
        setGeneratedImageDataUrl(null); // Clear previous image
        setIsImageGenerationModalOpen(true);
    }, []);
    const closeImageGenerationModal = useCallback(() => {
        setIsImageGenerationModalOpen(false);
        // setImageGenerationPrompt(''); // Keep prompt for re-opening same context? Or clear? Cleared on open.
        // setGeneratedImageDataUrl(null); // Cleared on open
    }, []);
    
    // Legacy Cover Modal states (can be mapped to new generic modal if desired)
    const [isCoverModalOpen, setIsCoverModalOpen] = useState(false);
    const [coverPrompt, setCoverPrompt] = useState('');
    const [generatedCoverImage, setGeneratedCoverImage] = useState<string | null>(null); // State variable

    const openCoverModal = useCallback(() => { // This now delegates to the new generic modal
        openImageGenerationModal('coverArt', coverPrompt || ''); 
        setIsCoverModalOpen(true); // Keep legacy flag for direct users
    }, [openImageGenerationModal, coverPrompt]);

    const closeCoverModal = useCallback(() => { // This now delegates to the new generic modal
        closeImageGenerationModal();
        setIsCoverModalOpen(false); // Keep legacy flag
        setGeneratedCoverImage(null); // Clear specific legacy state
        setCoverPrompt(''); // Clear specific legacy state
    }, [closeImageGenerationModal]);


    // Save As Modal
    const [isSaveAsModalOpen, setIsSaveAsModalOpen] = useState(false);
    const [saveAsProjectName, setSaveAsProjectName] = useState('');

    const openSaveAsModal = useCallback((currentProjectName?: string | null) => {
        setSaveAsProjectName(currentProjectName || 'My New Book');
        setIsSaveAsModalOpen(true);
    }, []);
    const closeSaveAsModal = useCallback(() => setIsSaveAsModalOpen(false), []);

    // Load Project Modal
    const [isLoadProjectModalOpen, setIsLoadProjectModalOpen] = useState(false);
    const openLoadProjectModal = useCallback(() => setIsLoadProjectModalOpen(true), []);
    const closeLoadProjectModal = useCallback(() => setIsLoadProjectModalOpen(false), []);
    
    // Split Chapter Modal
    const [isSplitChapterModalOpen, setIsSplitChapterModalOpen] = useState(false);
    const [chapterToSplit, setChapterToSplit] = useState<Chapter | null>(null);

    const openSplitChapterModal = useCallback((chapter: Chapter) => {
        setChapterToSplit(chapter);
        setIsSplitChapterModalOpen(true);
    }, []);
    const closeSplitChapterModal = useCallback(() => {
        setIsSplitChapterModalOpen(false);
        setChapterToSplit(null);
    }, []);

    // Merge Chapter Modal
    const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
    const [chaptersToDisplayInMergeModal, setChaptersToDisplayInMergeModal] = useState<Chapter[]>([]);

    const openMergeModal = useCallback((chapters: Chapter[]) => {
        setChaptersToDisplayInMergeModal(chapters);
        setIsMergeModalOpen(true);
    }, []);
    const closeMergeModal = useCallback(() => {
        setIsMergeModalOpen(false);
        setChaptersToDisplayInMergeModal([]);
    }, []);

    // Debug Response Modal
    const [isDebugResponseModalOpen, setIsDebugResponseModalOpen] = useState(false);
    const [llmRawResponseForDebug, setLlmRawResponseForDebug] = useState('');

    const openDebugResponseModal = useCallback((rawResponse: string) => {
        setLlmRawResponseForDebug(rawResponse);
        setIsDebugResponseModalOpen(true);
    }, []);
    const closeDebugResponseModal = useCallback(() => {
        setIsDebugResponseModalOpen(false);
        setLlmRawResponseForDebug(''); 
    }, []);


    return {
        isApiKeyModalOpen, apiKeyInputValue, openaiApiKeyInputValue, deepseekApiKeyInputValue,
        openApiKeyModal, closeApiKeyModal, setApiKeyInputValue,
        setOpenaiApiKeyInputValue, setDeepseekApiKeyInputValue,

        isImageGenerationModalOpen, imageGenerationPrompt, generatedImageDataUrl, imageGenerationContext,
        openImageGenerationModal, closeImageGenerationModal, setImageGenerationPrompt, setGeneratedImageDataUrl,
        setIsImageGenerationModalOpen,

        isSaveAsModalOpen, saveAsProjectName, openSaveAsModal, closeSaveAsModal, setSaveAsProjectName,
        isLoadProjectModalOpen, openLoadProjectModal, closeLoadProjectModal,
        isSplitChapterModalOpen, chapterToSplit, openSplitChapterModal, closeSplitChapterModal,
        isMergeModalOpen, chaptersToDisplayInMergeModal, openMergeModal, closeMergeModal,
        isDebugResponseModalOpen, llmRawResponseForDebug, openDebugResponseModal, closeDebugResponseModal,

        // Legacy (for ProjectHeader for now)
        isCoverModalOpen, coverPrompt, generatedCoverImage, openCoverModal, closeCoverModal, setCoverPrompt,
        setGeneratedCoverImage, // ADDED setter to returned object
    };
};